import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  FormControlLabel,
  Switch,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from "../../components/Alert";
import {
  Router as EndpointIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  PlayArrow as TestIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@material-ui/icons';
import { green, red, orange, blue } from '@material-ui/core/colors';
import { AuthContext } from '../../context/Auth/AuthContext';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 600,
      maxWidth: 800,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  formContainer: {
    padding: theme.spacing(2, 0),
  },
  fieldGroup: {
    marginBottom: theme.spacing(3),
  },
  fieldGroupTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  formGrid: {
    gap: theme.spacing(2),
  },
  userSelection: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  userList: {
    maxHeight: 200,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
  },
  userItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedUser: {
    backgroundColor: theme.palette.action.selected,
  },
  testSection: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  testResult: {
    marginTop: theme.spacing(2),
  },
  successResult: {
    color: green[600],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  errorResult: {
    color: red[600],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  warningResult: {
    color: orange[600],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statusChip: {
    fontWeight: 600,
  },
  activeChip: {
    backgroundColor: green[100],
    color: green[800],
  },
  inactiveChip: {
    backgroundColor: red[100],
    color: red[800],
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  testButton: {
    backgroundColor: blue[500],
    color: 'white',
    '&:hover': {
      backgroundColor: blue[700],
    },
  },
  saveButton: {
    backgroundColor: green[500],
    color: 'white',
    '&:hover': {
      backgroundColor: green[700],
    },
  },
  helpText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  endpointPreview: {
    backgroundColor: blue[50],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  previewTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  previewContent: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const EndpointForm = ({
  open,
  onClose,
  endpoint = null,
  onSave,
  onTest,
  connections = [],
  users = [],
  isEdit = false,
  isLoading = false
}) => {
  const classes = useStyles();

  const [formData, setFormData] = useState({
    name: '',
    technology: 'PJSIP',
    endpoint: '',
    userId: '',
    connectionId: '',
    isActive: true,
    context: 'default',
    transport: 'udp',
    codec: 'ulaw,alaw,g722',
    dtmfMode: 'rfc2833',
    directMedia: true,
    mailbox: '',
  });

  const [errors, setErrors] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);

  const technologies = [
    { value: 'PJSIP', label: 'PJSIP (Recomendado)' },
    { value: 'SIP', label: 'SIP (Legacy)' },
    { value: 'IAX2', label: 'IAX2' },
    { value: 'DAHDI', label: 'DAHDI' },
  ];

  const transports = [
    { value: 'udp', label: 'UDP' },
    { value: 'tcp', label: 'TCP' },
    { value: 'tls', label: 'TLS' },
    { value: 'ws', label: 'WebSocket' },
    { value: 'wss', label: 'WebSocket Secure' },
  ];

  const dtmfModes = [
    { value: 'rfc2833', label: 'RFC2833 (Recomendado)' },
    { value: 'inband', label: 'Inband' },
    { value: 'info', label: 'SIP INFO' },
  ];

  useEffect(() => {
    if (endpoint) {
      setFormData({
        name: endpoint.name || '',
        technology: endpoint.technology || 'PJSIP',
        endpoint: endpoint.endpoint || '',
        userId: endpoint.userId || '',
        connectionId: endpoint.connectionId || '',
        isActive: endpoint.isActive !== undefined ? endpoint.isActive : true,
        context: endpoint.context || 'default',
        transport: endpoint.transport || 'udp',
        codec: endpoint.codec || 'ulaw,alaw,g722',
        dtmfMode: endpoint.dtmfMode || 'rfc2833',
        directMedia: endpoint.directMedia !== undefined ? endpoint.directMedia : true,
        mailbox: endpoint.mailbox || '',
      });

      if (endpoint.userId) {
        const user = users.find(u => u.id === endpoint.userId);
        setSelectedUser(user);
      }
    } else {
      setFormData({
        name: '',
        technology: 'PJSIP',
        endpoint: '',
        userId: '',
        connectionId: connections[0]?.id || '',
        isActive: true,
        context: 'default',
        transport: 'udp',
        codec: 'ulaw,alaw,g722',
        dtmfMode: 'rfc2833',
        directMedia: true,
        mailbox: '',
      });
      setSelectedUser(null);
    }
    setErrors({});
    setTestResult(null);
  }, [endpoint, open, connections, users]);

  useEffect(() => {
    // Filter users that don't have endpoints yet
    const usersWithoutEndpoints = users.filter(user =>
      !user.asteriskEndpoint || (isEdit && user.id === endpoint?.userId)
    );
    setAvailableUsers(usersWithoutEndpoints);
  }, [users, endpoint, isEdit]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Auto-generate endpoint name and mailbox when user is selected
    if (field === 'userId') {
      const user = users.find(u => u.id === value);
      if (user) {
        setSelectedUser(user);
        setFormData(prev => ({
          ...prev,
          name: user.name,
          endpoint: `${user.name.toLowerCase().replace(/\s+/g, '_')}_${value}`,
          mailbox: `${value}@default`,
        }));
      } else {
        setSelectedUser(null);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = i18n.t('asterisk.forms.validation.nameRequired');
    }

    if (!formData.endpoint.trim()) {
      newErrors.endpoint = i18n.t('asterisk.forms.validation.endpointRequired');
    }

    if (!formData.userId) {
      newErrors.userId = i18n.t('asterisk.forms.validation.userRequired');
    }

    if (!formData.connectionId) {
      newErrors.connectionId = i18n.t('asterisk.forms.validation.connectionRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setFormData(prev => ({
      ...prev,
      userId: user.id,
      name: user.name,
      endpoint: `${user.name.toLowerCase().replace(/\s+/g, '_')}_${user.id}`,
      mailbox: `${user.id}@default`,
    }));
  };

  const handleTest = async () => {
    if (!validateForm()) return;

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await onTest(formData);
      setTestResult({
        type: result.success ? 'success' : 'error',
        message: result.message,
        details: result.details
      });
    } catch (error) {
      setTestResult({
        type: 'error',
        message: i18n.t('asterisk.endpointTestFailed'),
        details: error.message
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      setTestResult({
        type: 'error',
        message: i18n.t('asterisk.saveEndpointFailed'),
        details: error.message
      });
    }
  };

  const generateEndpointConfig = () => {
    if (!formData.endpoint) return '';

    return `[${formData.endpoint}]
type=endpoint
context=${formData.context}
disallow=all
allow=${formData.codec}
transport=${formData.transport}
dtmf_mode=${formData.dtmfMode}
direct_media=${formData.directMedia ? 'yes' : 'no'}
${formData.mailbox ? `mailboxes=${formData.mailbox}` : ''}
; User: ${selectedUser?.name || formData.name}
; Technology: ${formData.technology}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box className={classes.header}>
          <EndpointIcon color="primary" />
          <Typography variant="h6">
            {isEdit ? i18n.t('asterisk.editEndpoint') : i18n.t('asterisk.newEndpoint')}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            style={{ marginLeft: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box className={classes.formContainer}>
          {/* User Selection */}
          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldGroupTitle}>
              {i18n.t('asterisk.userAssignment')}
            </Typography>

            {!isEdit && (
              <Card className={classes.userSelection}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {i18n.t('asterisk.selectUser')}
                  </Typography>
                  <List className={classes.userList}>
                    {availableUsers.map((user) => (
                      <ListItem
                        key={user.id}
                        className={`${classes.userItem} ${
                          selectedUser?.id === user.id ? classes.selectedUser : ''
                        }`}
                        onClick={() => handleUserSelect(user)}
                      >
                        <ListItemIcon>
                          <PersonIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={user.name}
                          secondary={`${user.email} - ID: ${user.id}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {availableUsers.length === 0 && (
                    <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: 16 }}>
                      {i18n.t('asterisk.noUsersAvailable')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {selectedUser && (
              <Alert severity="info" style={{ marginTop: 16 }}>
                <Typography variant="body2">
                  <strong>{i18n.t('asterisk.selectedUser')}:</strong> {selectedUser.name} ({selectedUser.email})
                </Typography>
              </Alert>
            )}
          </Box>

          {/* Basic Configuration */}
          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldGroupTitle}>
              {i18n.t('asterisk.basicConfiguration')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={i18n.t('asterisk.endpointName')}
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name || i18n.t('asterisk.endpointNameHelp')}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel>{i18n.t('asterisk.technology')}</InputLabel>
                  <Select
                    value={formData.technology}
                    onChange={handleInputChange('technology')}
                    label={i18n.t('asterisk.technology')}
                  >
                    {technologies.map((tech) => (
                      <MenuItem key={tech.value} value={tech.value}>
                        {tech.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={i18n.t('asterisk.endpointIdentifier')}
                  value={formData.endpoint}
                  onChange={handleInputChange('endpoint')}
                  error={!!errors.endpoint}
                  helperText={errors.endpoint || i18n.t('asterisk.endpointIdentifierHelp')}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel>{i18n.t('asterisk.connection')}</InputLabel>
                  <Select
                    value={formData.connectionId}
                    onChange={handleInputChange('connectionId')}
                    label={i18n.t('asterisk.connection')}
                    error={!!errors.connectionId}
                  >
                    {connections.map((connection) => (
                      <MenuItem key={connection.id} value={connection.id}>
                        {connection.name} ({connection.host}:{connection.port})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Advanced Configuration */}
          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldGroupTitle}>
              {i18n.t('asterisk.advancedConfiguration')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={i18n.t('asterisk.context')}
                  value={formData.context}
                  onChange={handleInputChange('context')}
                  variant="outlined"
                  helperText={i18n.t('asterisk.contextHelp')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>{i18n.t('asterisk.transport')}</InputLabel>
                  <Select
                    value={formData.transport}
                    onChange={handleInputChange('transport')}
                    label={i18n.t('asterisk.transport')}
                  >
                    {transports.map((transport) => (
                      <MenuItem key={transport.value} value={transport.value}>
                        {transport.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={i18n.t('asterisk.codec')}
                  value={formData.codec}
                  onChange={handleInputChange('codec')}
                  variant="outlined"
                  helperText={i18n.t('asterisk.codecHelp')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>{i18n.t('asterisk.dtmfMode')}</InputLabel>
                  <Select
                    value={formData.dtmfMode}
                    onChange={handleInputChange('dtmfMode')}
                    label={i18n.t('asterisk.dtmfMode')}
                  >
                    {dtmfModes.map((mode) => (
                      <MenuItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={i18n.t('asterisk.mailbox')}
                  value={formData.mailbox}
                  onChange={handleInputChange('mailbox')}
                  variant="outlined"
                  helperText={i18n.t('asterisk.mailboxHelp')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.directMedia}
                      onChange={handleInputChange('directMedia')}
                      color="primary"
                    />
                  }
                  label={i18n.t('asterisk.directMedia')}
                />
                <Typography className={classes.helpText}>
                  {i18n.t('asterisk.directMediaHelp')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleInputChange('isActive')}
                      color="primary"
                    />
                  }
                  label={i18n.t('asterisk.isActive')}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Configuration Preview */}
          {formData.endpoint && (
            <Box className={classes.endpointPreview}>
              <Typography className={classes.previewTitle}>
                <SettingsIcon />
                {i18n.t('asterisk.configurationPreview')}
              </Typography>
              <Box className={classes.previewContent}>
                <pre>{generateEndpointConfig()}</pre>
              </Box>
            </Box>
          )}

          {/* Test Section */}
          <Box className={classes.testSection}>
            <Typography variant="subtitle1" gutterBottom>
              {i18n.t('asterisk.testEndpoint')}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {i18n.t('asterisk.testEndpointHelp')}
            </Typography>

            <Box className={classes.actionButtons}>
              <Button
                className={classes.testButton}
                onClick={handleTest}
                disabled={isTesting || !formData.endpoint}
                startIcon={isTesting ? <CircularProgress size={20} /> : <TestIcon />}
              >
                {isTesting ? i18n.t('asterisk.testing') : i18n.t('asterisk.testEndpoint')}
              </Button>
            </Box>

            {testResult && (
              <Box className={classes.testResult}>
                <Alert severity={testResult.type}>
                  <Typography variant="body2">
                    {testResult.message}
                  </Typography>
                  {testResult.details && (
                    <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                      {testResult.details}
                    </Typography>
                  )}
                </Alert>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {i18n.t('asterisk.cancel')}
        </Button>
        <Button
          className={classes.saveButton}
          onClick={handleSave}
          disabled={isLoading || !selectedUser}
          startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {isLoading
            ? i18n.t('asterisk.saving')
            : isEdit
              ? i18n.t('asterisk.updateEndpoint')
              : i18n.t('asterisk.createEndpoint')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EndpointForm;